import { GroupResponse } from "@/app/api/types/group";
import { useUserOptional } from "@/components/auth-provider";
import { useMyGroups } from "@/query/hooks/useGroups";

/** Which `/company` surface the current persona should see. */
export type CompanyView = "hub" | "workspace" | "empty" | "error";

interface CompanyPersona {
  /**
   * Resolved view (only meaningful when `isResolving` is false):
   * - `hub` — the company owner: the full company hub.
   * - `workspace` — a non-owner who belongs to ≥1 group: their group workspace
   *   (actions inside are gated per group role).
   * - `empty` — a non-owner who belongs to no group: no access.
   * - `error` — a hard groups fetch failure: can't resolve the persona safely.
   */
  view: CompanyView;
  /** The viewer's own groups — the workspace's list (switcher when several) and
   *  the hub's Groups tab. Undefined until the groups query resolves. */
  myGroups: GroupResponse[] | undefined;
  /** True while the groups query hasn't resolved the view yet. */
  isResolving: boolean;
}

/**
 * Derives the `/company` persona from permissions alone — NOT from the
 * `groups_are_agencies` flag. The company owner gets the hub; everyone else sees
 * the group(s) they belong to (workspace), or an empty state with no group. The
 * flag only affects vocabulary (agency vs group labels via `useGroupT`), never
 * which surface you land on — so there is no flag-driven fail-closed gate here.
 *
 * `enabled` gates the underlying groups fetch AND persona resolution — it is
 * `false` for the company owner and off `/company` (pass
 * `pathname.startsWith("/company")` in the app header), so the header doesn't
 * query groups on every route. The query key is shared, so page + header dedupe.
 */
export function useCompanyPersona(options?: {
  enabled?: boolean;
}): CompanyPersona {
  const user = useUserOptional();
  const isOwner = user?.company_permissions === 1;
  // Only non-owners on the relevant surface resolve a non-hub persona.
  const enabled = (options?.enabled ?? true) && !isOwner;
  const {
    data: myGroups,
    isLoading: myGroupsLoading,
    isError: myGroupsError,
  } = useMyGroups({ enabled });

  const { view, isResolving } = resolvePersonaView({
    enabled,
    myGroupsError,
    myGroupsLoading,
    hasGroups: (myGroups ?? []).length > 0,
  });

  return { view, myGroups, isResolving };
}

/**
 * Pure persona decision:
 * - not enabled (owner / off-surface) → hub.
 * - a groups error → error (rather than a false "no access").
 * - still loading → hub optimistically, but `isResolving` so the caller waits.
 * - resolved → workspace if the viewer has any group, else empty.
 */
function resolvePersonaView(input: {
  enabled: boolean;
  myGroupsError: boolean;
  myGroupsLoading: boolean;
  hasGroups: boolean;
}): { view: CompanyView; isResolving: boolean } {
  if (!input.enabled) return { view: "hub", isResolving: false };
  if (input.myGroupsError) return { view: "error", isResolving: false };
  if (input.myGroupsLoading) return { view: "hub", isResolving: true };
  return {
    view: input.hasGroups ? "workspace" : "empty",
    isResolving: false,
  };
}
